import { Box, IconButton, Popover, Typography } from '@mui/material';
import { addMonths, format, isValid, setDate, subMonths } from 'date-fns';
import { Info as InfoIcon } from '@mui/icons-material';
import ReactSpeedometer from 'react-d3-speedometer';
import { useState } from 'react';

const getNextReferenceDate = (dateString) => {
  // Parse the input date string to a Date object
  const date = new Date(dateString);

  // Add one month to the date
  const nextMonthDate = addMonths(date, 1);

  // Set the date to the 1st day of the next month
  const nextReferenceDate = setDate(nextMonthDate, 1);

  // Format the date as 'DD/MMM/YYYY'
  return format(nextReferenceDate, 'dd-MMM-yyyy');
};

const PerformanceScoreCard = ({ breakpoints, score, position = "relative" }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const getLastMonthString = (dateString) => {
    const date = new Date(dateString);
    const lastMonth = subMonths(date, 1);
    return format(lastMonth, 'MMMM yyyy');
  };

  if (!breakpoints || !score) return null;
  const { totalPoints, colors, userPoints, text, pointSystem } = score;

  const updatedBreakpoints = [
    breakpoints[0],
    breakpoints[0] + 0.000000000001,
    ...breakpoints.slice(1),
    totalPoints + 0.000000000001,
  ];

  const speedometerValue = Math.max(0, Math.min(userPoints?.totalPoints, totalPoints));

  let userCategoryIndex;

  if (userPoints?.totalPoints >= 0 && userPoints?.totalPoints <= 200) {
    userCategoryIndex = breakpoints.findIndex(
      (category, index) =>
        userPoints?.totalPoints >= breakpoints[index] && userPoints?.totalPoints < breakpoints[index + 1]
    );
  } else if (userPoints?.totalPoints < 0) {
    userCategoryIndex = 0;
  } else if (userPoints?.totalPoints > 200) {
    userCategoryIndex = 3;
  }

  const userCategory = pointSystem[userCategoryIndex];

  const labels = pointSystem.map((data, i) => ({
    text: `${data.rating}`,
    position: 'OUTSIDE',
    fontSize: data.rating === userCategory?.rating ? '16px' : '14px',
    color: data.rating === userCategory?.rating ? '#4682b4' : 'black',
  }));

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  // labels.push({ text: totalPoints, position: 'INSIDE', fontSize: '12px' });
  // labels.unshift({ text: '0', position: 'INSIDE', fontSize: '12px' });
  // colors.push({ color: 'green' });
  // colors.unshift({ color: 'red' });

  return (
    <Box position={position} top={10} left={80} width={270} sx={{ boxSizing: 'border-box' }}>
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ pb: 2 }}>
        <Typography variant="h6" sx={{ mr: 1 }}>
          {/* Performance Score */}
          {`Score Card -${userPoints ? `${getLastMonthString(userPoints.updatedAt)}` : 'N/A'}`}
        </Typography>
        <IconButton onClick={handlePopoverOpen}>
          <InfoIcon />
        </IconButton>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Typography sx={{ p: 2 }}>
            <Typography variant="h6">Score System Levels:</Typography>
            <br />

            <Typography variant="caption" fontSize="16px">
              1. Average (0-50)
            </Typography>
            <br />
            <Typography variant="caption" fontSize="16px">
              2. Good (51-100)
            </Typography>
            <br />
            <Typography variant="caption" fontSize="16px">
              3. Very Good (101-150)
            </Typography>
            <br />
            <Typography variant="caption" fontSize="16px">
              4. Superb (151-200)
            </Typography>
            <br />
            <br />
            <Typography variant="caption">
              Your score is calculated based on your performance and activities over the past month.
            </Typography>
          </Typography>
        </Popover>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <ReactSpeedometer
          height={150}
          width={270}
          maxValue={totalPoints}
          minValue={breakpoints[0]}
          customSegmentStops={breakpoints}
          maxSegmentLabels={0}
          customSegmentLabels={labels}
          segmentColors={breakpoints.map((_, i) => colors[i]?.color)}
          value={speedometerValue}
          needleColor="steelblue"
          needleHeightRatio={0.7}
          ringWidth={60}
          needleTransitionDuration={5000}
          needleTransition="easeElastic"
          textColor="black"
          currentValuePlaceholderStyle={null}
          // currentValueText={userPoints ? `Score: ${userPoints?.totalPoints.toFixed(2)}` : 'Score Not Found'}
          currentValueText=" "
          labelFontSize="12px"
          valueTextFontSize="14px"
        />
        <Typography variant="h6" sx={{ mb: 1 }}>
          {userPoints ? `Score: ${userPoints?.totalPoints.toFixed(2)}` : 'Score Not Found'}
        </Typography>
      </Box>

      {userPoints && (
        <Box padding={0} margin={0} bgcolor={'transparent'}>
          <Box display="flex" justifyContent="space-between">
            <DateInfoBox label="Last Updated On" date={new Date(userPoints?.updatedAt)} dateFormat="dd-MMM-yyyy" />
            <DateInfoBox
              label="Next Refresh On"
              date={getNextReferenceDate(new Date(userPoints?.updatedAt))}
              dateFormat="dd-MMM-yyyy"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

const DateInfoBox = ({ label, date, dateFormat = 'dd-MMM-yyyy' }) => {
  let formattedDate = 'N/A';

  if (date && isValid(new Date(date))) {
    try {
      formattedDate = format(new Date(date), dateFormat);
    } catch (error) {
      console.error('Error formatting date:', error);
    }
  }

  return (
    <Box mb={1} width={'100%'}>
      <Typography variant="body2" align="center " fontSize={12}>
        {label}
      </Typography>
      <Typography variant={'body1'} fontSize={12} fontWeight={'Bold'} align="center" sx={{ mr: 1 }}>
        {formattedDate}
      </Typography>
    </Box>
  );
};
export default PerformanceScoreCard;
